import _ from 'lodash';
import { makeAutoObservable, observable, runInAction } from 'mobx';
import { WarrantI18N } from '../../models';
import apiManage from '../../request';
import i18n from '../../i18n';
import GlobalNotification from '../../component/common/snackbar';
import { checkCardFromRule } from '../../component/card/card-from';
import { productCardArr } from './product';

/**
 * 产品详情数据管理
 */
export class ProductState implements ProductInfoData {

    constructor(props: ProductProps) {
        makeAutoObservable(this);
        this.id = props.id;
        this.componentType = props.componentType;
    }


    // *********************
    // Attributes
    // *********************


    productEditDetail: WarrantI18N & LanguageInfo = {};
    // 组件类型
    componentType: 'EDIT' | 'PREVIEW' | 'ADD' | undefined = 'ADD';
    // 产品_id
    id: string | undefined = '';
    // 是否启用骨架屏加载  默认进来页面开启
    isEnableSkeleton: boolean = true;
    // 编辑输入框操作map
    @observable operateMap: Map<string, { origin: number, current: number }> = new Map();
    originProductData: WarrantI18N & LanguageInfo = {};
    // 输入框是否可以输入编辑
    isEditInput: boolean = false;
    // 输入框是否为空校验
    isEmptyVerify: boolean = false;
    // 保存按钮禁用
    disabledBtn: number = 0;
    // 负责人信息弹窗配置
    dialogConfig: {
        // 打开弹窗
        visible: boolean;
        // 弹窗标题
        title?: string;
        // 关闭弹窗
        onClose?: () => void;
    } = {
            visible: false,
            title: '',
            onClose: () => { },
        }



    // *********************
    // Business Function
    // *********************

    // 初始化
    async init() {
        // 编辑和请求才请求数据
        if (this.componentType !== 'ADD' && this.id) {
            // 获取账号数据
            await this.getProductData(this.id);
            // 关闭骨架屏
            runInAction(() => {
                this.isEnableSkeleton = false;
            })
        }
    }


    // 检查输入框必填项是否为空
    checkIsEmpty() {
        const { price } = this.productEditDetail;
        if (!price) {
            GlobalNotification.error(`${i18n.t('create_group_component_message_invalid_group_name')}${i18n.t('general_text_incompatible_rules')}`);
            return true
        }
        return false
    }

    // 设置为空标识
    setIsEmptyFlag(value: boolean) {
        this.isEmptyVerify = value;
    }

    // 校验表单是否修改
    checkFormIsEdited() {
        if (this.operateMap.size !== 0) {
            return true;
        } else {
            return false;
        }
    }


    // 开启输入框禁用和按钮禁用加载
    enableForbid() {
        this.disabledBtn = 1;
        this.isEditInput = true;
    }

    // 关闭输入框禁用和按钮禁用加载
    closeForbid() {
        this.disabledBtn = 0;
        this.isEditInput = false;
    }

    // 显示通知信息
    showNotificationMessage(message: string, type: 'success' | 'error') {
        type === 'success' ? GlobalNotification.success(message) : GlobalNotification.error(message);
    }

    // 改变文本框输入
    handleChangeText(value: string, key: string): void {
        const curKeyVal = value;
        const originKeyVal = this.originProductData[key as keyof WarrantI18N & LanguageInfo];
        // 判断数据是否编辑过
        if (originKeyVal !== curKeyVal) {
            // _.set(this.groupEditDetail, [key], curKeyVal);
            this.operateMap.set(key, { origin: Number(originKeyVal), current: Number(curKeyVal) })
        }
        else this.operateMap.delete(key);
        _.set(this.productEditDetail, [key], curKeyVal);
    }


    /**
     * 产品保存函数
     * TODO 目前只有编辑
     * @param saveType 保存类型: 新增保存 | 编辑保存
     * @returns
     */
    async onSave(saveType: 'ADD' | 'EDIT') {
        console.log(saveType);
        // 成功标识
        let successFlag: boolean = true;
        try {
            // 检查必填项  校验必填规则
            const errorList = checkCardFromRule({ config: productCardArr, data: this.productEditDetail as {} });
            this.setIsEmptyFlag(true);
            if (errorList?.length) {
                return;
            }
            // 源数据
            const originProduct = this.originProductData;

            const { name_IT, name_EN, name_CN, name_SP, description_IT, description_EN, description_CN, description_SP, price } = this.productEditDetail;

            const name = [
                {
                    key: 'CN',
                    value: name_CN
                },
                {
                    key: 'IT',
                    value: name_IT
                },
                {
                    key: 'EN',
                    value: name_EN
                },
                {
                    key: 'SP',
                    value: name_SP
                },
            ];

            const description = [
                {
                    key: 'CN',
                    value: description_CN
                },
                {
                    key: 'IT',
                    value: description_IT
                },
                {
                    key: 'EN',
                    value: description_EN
                },
                {
                    key: 'SP',
                    value: description_SP
                }
            ];

            // 保存所用的参数
            // 表单有更改才发送有变化的数据
            const params: WarrantI18N = {
                price: Number(price),
                description: description,
                name: name
            }
            // 开启编辑禁用
            this.enableForbid();
            // 大于1标识有改动 集团修改
            if (this.checkFormIsEdited()) {
                const result = await apiManage.patch_product_id_id_info(originProduct._id || '', params);
                // 请求成功
                if (!result?.error) {
                    this.showNotificationMessage(i18n.t('group_info_page_message_successfully_modify_group_information'), 'success')
                    // 刷新界面数据
                    this.getProductData(originProduct._id ?? '');
                } else {
                    this.showNotificationMessage(i18n.t('group_info_page_message_failed_modify_group_information'), 'error');
                }
            }
            // 关闭编辑禁用
            this.closeForbid();

            this.operateMap.clear();
            return successFlag
        } catch (error) {
            // console.log('errr ', error)
            successFlag = false;
            this.operateMap.clear();
            this.showNotificationMessage(i18n.t('group_info_page_message_failed_modify_group_information'), 'error');
            return successFlag
        }
    }

    /**
     * 获取
     */
    async getProductData(id: string) {
        /**
         * 1.设置标记状态为禁用
         * 2.成功之后才可以编辑
         * 3.关闭骨架屏
         */
        try {
            const result = await apiManage.get_product_id_id(id);
            if (!result?.error) {
                // 数据组装保存
                const productData: WarrantI18N & LanguageInfo = {
                    name_IT: result.name?.find(item => item.key === 'IT')?.value ?? '',
                    name_EN: result.name?.find(item => item.key === 'EN')?.value ?? '',
                    name_CN: result.name?.find(item => item.key === 'CN')?.value ?? '',
                    name_SP: result.name?.find(item => item.key === 'SP')?.value ?? '',
                    description_IT: result.description?.find(item => item.key === 'IT')?.value ?? '',
                    description_EN: result.description?.find(item => item.key === 'EN')?.value ?? '',
                    description_CN: result.description?.find(item => item.key === 'CN')?.value ?? '',
                    description_SP: result.description?.find(item => item.key === 'SP')?.value ?? '',
                    ...result
                }
                // 源数据
                const originProductData = productData;
                // 设置界面数据
                this.setProductData(productData, originProductData);
            } else {
                // 获取失败
                GlobalNotification.error(`${i18n.t('group_info_page_message_failed_obtain_group_information')}`)
            }
        } catch (error) {
            GlobalNotification.error(`${i18n.t('group_info_page_message_failed_obtain_group_information')}`)
        }
    }


    // 设置集团账号界面数据
    setProductData(productData: WarrantI18N & LanguageInfo, originData: WarrantI18N & LanguageInfo) {
        this.productEditDetail = productData;
        // 设置源数据 用于比对数据是否修改
        this.originProductData = originData;
    }

}

type ProductProps = {
    // 产品_id
    id?: string;
    // 组件类型
    componentType?: 'EDIT' | 'PREVIEW' | 'ADD';
}

type ProductInfoData = {
    // 产品编辑信息
    productEditDetail: WarrantI18N & LanguageInfo;

}

type LanguageInfo = {
    name_IT?: string;
    name_EN?: string;
    name_CN?: string;
    name_SP?: string;
    description_IT?: string;
    description_EN?: string;
    description_CN?: string;
    description_SP?: string;
}

